import Accordion from '../ui/Accordion'
import Button from '../ui/Button'
import { RichText } from '../ui/RichText'
import { headingReveal } from '../../animations/gsapAnimations'
import { useGsapContext } from '../../hooks/useGsapReveal'
import { useSection } from '../../cms/cms-context'
import { useFaqs } from '../../hooks/useFaqs'

/* The questions the desk answers on the phone every day, on the front page.
 *
 * They already lived on /financing, which is the right place for somebody
 * who came looking for financing — and the wrong place for somebody who is
 * three sections deep in the cars and has one thing left to ask. This is the
 * same accordion, put where the doubt actually happens: after the reviews,
 * before they leave.
 *
 * The questions themselves are records the console keeps — Storefront →
 * Questions — so a new one is up the afternoon it is asked. What stays in the
 * page builder is this band's own copy: the label, the headline and the
 * button beside it. See useFaqs for what happens before the desk has filed
 * anything, and when the API cannot be reached.
 */

function Faqs() {
  const cms = useSection()
  const { faqs } = useFaqs()

  const head = useGsapContext<HTMLDivElement>((scope) => headingReveal(scope))

  const accent = cms.text('titleAccent', 'before you sign')

  return (
    <section
      id="faqs"
      aria-labelledby="faqs-title"
      className="border-y border-line band-surface py-20 sm:py-28"
    >
      <div className="mx-auto w-full max-w-[1320px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-20">
          {/* ---------- headline ---------- */}
          <div ref={head} className="lg:sticky lg:top-28 lg:self-start">
            <p
              data-heading="eyebrow"
              {...cms.field('eyebrow')}
              className="flex items-center gap-3 label text-lime"
            >
              <span aria-hidden="true" data-heading="rule" className="h-px w-9 coachline" />
              {cms.text('eyebrow', 'Questions')}
            </p>
            <h2
              id="faqs-title"
              data-heading="title"
              {...cms.field('title')}
              className="mt-5 font-display text-display-2 text-platinum"
            >
              {cms.text('title', 'Everything worth asking')}
              {accent ? (
                <>
                  {' '}
                  <span className="text-lime-grad">{accent}</span>
                </>
              ) : null}
            </h2>
            <p
              data-heading="body"
              {...cms.field('lede')}
              className="mt-5 max-w-[44ch] text-[15.5px] leading-relaxed text-steel"
            >
              {cms.text(
                'lede',
                'Markup, deposits, documents and timing — answered the way we answer them on the phone. Anything else, ask us directly.',
              )}
            </p>

            <Button
              variant="ghost"
              size="lg"
              to={cms.text('ctaHref', '/contact')}
              arrow
              data-heading="body"
              {...cms.field('cta')}
              className="mt-9"
            >
              {cms.text('cta', 'Ask us anything')}
            </Button>
          </div>

          {/* ---------- the questions ---------- */}
          <Accordion
            className="lg:pt-2"
            defaultOpenId={faqs[0]?.id}
            items={faqs.map((faq) => ({
              id: faq.id,
              question: faq.question,
              answer: <RichText html={faq.html} />,
            }))}
          />
        </div>
      </div>
    </section>
  )
}

export default Faqs
